import { Trans, useLingui } from "@lingui/react/macro"
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core"
import clsx from "clsx"
import { useCallback, useEffect, useState } from "react"
import type { IJournalEntry } from "../../../lib/api/canon"
import type { ICharacterRef, IPendingItem } from "../interfaces"
import classes from "../styles.module.css"
import type { IViewProps } from "./interfaces"

function isJournal(item: IPendingItem): item is { kind: "journal"; entry: IJournalEntry } {
  return item.kind === "journal"
}

/**
 * Dumb view for CanonReviewPane. The smart wrapper at ../index.tsx owns the
 * fetch/mutate cycle for journal + chronicle entries; this component renders
 * the triage queue (badges, plaque images, accept/reject/edit/reassign
 * controls), holds presentation-only state (active index, edit/reassign modal
 * targets), and emits intent via callback props.
 *
 * Keyboard nav (J/K/A/R/E/Shift+R) is also a view concern — it only mutates
 * local UI state plus calls the supplied callbacks.
 */
export function CanonReviewPaneView({
  items,
  characters,
  characterNameById,
  loading,
  onAccept,
  onReject,
  onEditSave,
  onReassign,
}: IViewProps): React.JSX.Element {
  const { t } = useLingui()
  const [activeIndex, setActiveIndex] = useState(0)
  const [editTarget, setEditTarget] = useState<IPendingItem | null>(null)
  const [reassignTarget, setReassignTarget] = useState<IPendingItem | null>(null)

  useEffect(() => {
    if (activeIndex >= items.length && items.length > 0) {
      setActiveIndex(0)
    }
  }, [activeIndex, items.length])

  const accept = useCallback(
    (item: IPendingItem) => {
      onAccept(item)
    },
    [onAccept],
  )

  const reject = useCallback(
    (item: IPendingItem) => {
      onReject(item)
    },
    [onReject],
  )

  // Keyboard shortcuts — only active when no modal is open and there are items.
  useEffect(() => {
    if (editTarget || reassignTarget) return
    if (!items.length) return

    function handler(ev: KeyboardEvent): void {
      const target = ev.target as HTMLElement | null
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return
      }

      const active = items[activeIndex]
      if (!active) return

      switch (ev.key.toLowerCase()) {
        case "j":
          ev.preventDefault()
          setActiveIndex((i) => Math.min(items.length - 1, i + 1))
          break
        case "k":
          ev.preventDefault()
          setActiveIndex((i) => Math.max(0, i - 1))
          break
        case "a":
          ev.preventDefault()
          accept(active)
          break
        case "r":
          ev.preventDefault()
          if (ev.shiftKey && isJournal(active)) {
            setReassignTarget(active)
          } else {
            reject(active)
          }
          break
        case "e":
          ev.preventDefault()
          setEditTarget(active)
          break
      }
    }

    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [items, activeIndex, accept, reject, editTarget, reassignTarget])

  if (loading) {
    return (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    )
  }

  if (!items.length) {
    return (
      <Card withBorder padding="lg">
        <Stack align="center" gap="xs">
          <Text c="dimmed" fz="sm">
            <Trans>No new canon to review.</Trans>
          </Text>
          <Text c="dimmed" fz="xs">
            <Trans>The next session you finalize will surface its proposed entries here.</Trans>
          </Text>
        </Stack>
      </Card>
    )
  }

  return (
    <div className={classes.root}>
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={4}>
            <Trans>Canon to review</Trans>{" "}
            <Badge size="lg" variant="light" color="warning" ml="xs">
              {items.length}
            </Badge>
          </Title>
          <Text fz="xs" c="dimmed">
            <Trans>Accept what's true, edit what's close, reject what's wrong.</Trans>
          </Text>
        </div>
        <Text className={classes.shortcuts}>
          <span className={classes.shortcutKey}>J</span>/<span className={classes.shortcutKey}>K</span>{" "}
          <Trans>navigate</Trans> · <span className={classes.shortcutKey}>A</span> <Trans>accept</Trans> ·{" "}
          <span className={classes.shortcutKey}>R</span> <Trans>reject</Trans> ·{" "}
          <span className={classes.shortcutKey}>E</span> <Trans>edit</Trans> ·{" "}
          <span className={classes.shortcutKey}>⇧R</span> <Trans>reassign</Trans>
        </Text>
      </Group>

      <div className={classes.cardStack}>
        {items.map((item, idx) => {
          const e = item.entry
          const isActive = idx === activeIndex
          const charName = isJournal(item) ? characterNameById.get(item.entry.characterId) : null
          const plaqueUrl = e.boundImageUrl
          return (
            <Card
              key={e.id}
              withBorder
              padding="md"
              className={clsx(classes.entryCard, isActive && classes.entryCardActive)}
              onClick={() => setActiveIndex(idx)}
            >
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className={classes.entryHeader}>
                    <Badge color={isJournal(item) ? "info" : "brand"} size="sm" variant="light">
                      {isJournal(item) ? <Trans>journal</Trans> : <Trans>chronicle</Trans>}
                    </Badge>
                    <Badge size="sm" variant="outline" color="gray">
                      {e.entryType.replace("_", " ")}
                    </Badge>
                    {charName && (
                      <Badge size="sm" variant="dot" color="info">
                        {charName}
                      </Badge>
                    )}
                    {plaqueUrl && (
                      <Badge size="sm" variant="filled" color="highlight">
                        <Trans>plaque</Trans>
                      </Badge>
                    )}
                    <Text className={classes.entryTitle}>{e.title}</Text>
                  </div>
                  {plaqueUrl && <img src={plaqueUrl} alt={e.title} className={classes.plaqueImage} loading="lazy" />}
                  <Text className={classes.entryBody} mt={4}>
                    {e.body}
                  </Text>
                  {e.sourceQuote && <div className={classes.sourceQuote}>"{e.sourceQuote}"</div>}
                </div>
                <Group gap={4}>
                  <Tooltip label={t`Accept (A)`}>
                    <ActionIcon
                      variant="filled"
                      color="success"
                      onClick={(ev) => {
                        ev.stopPropagation()
                        accept(item)
                      }}
                      aria-label={t`Accept`}
                    >
                      ✓
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label={t`Edit (E)`}>
                    <ActionIcon
                      variant="light"
                      color="gray"
                      onClick={(ev) => {
                        ev.stopPropagation()
                        setEditTarget(item)
                      }}
                      aria-label={t`Edit`}
                    >
                      ✎
                    </ActionIcon>
                  </Tooltip>
                  {isJournal(item) && (
                    <Tooltip label={t`Reassign to a different character (Shift+R)`}>
                      <ActionIcon
                        variant="light"
                        color="info"
                        onClick={(ev) => {
                          ev.stopPropagation()
                          setReassignTarget(item)
                        }}
                        aria-label={t`Reassign`}
                      >
                        ↻
                      </ActionIcon>
                    </Tooltip>
                  )}
                  <Tooltip label={t`Reject (R)`}>
                    <ActionIcon
                      variant="light"
                      color="danger"
                      onClick={(ev) => {
                        ev.stopPropagation()
                        reject(item)
                      }}
                      aria-label={t`Reject`}
                    >
                      ✕
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
            </Card>
          )
        })}
      </div>

      <EditEntryModal
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={(patch) => {
          if (!editTarget) return
          onEditSave(editTarget, patch)
          setEditTarget(null)
        }}
      />

      <ReassignModal
        target={reassignTarget}
        characters={characters}
        onClose={() => setReassignTarget(null)}
        onConfirm={(characterId) => {
          if (!reassignTarget || !isJournal(reassignTarget)) return
          onReassign(reassignTarget, characterId)
          setReassignTarget(null)
        }}
      />
    </div>
  )
}

interface IEditModalProps {
  target: IPendingItem | null
  onClose: () => void
  onSave: (patch: { title?: string; body?: string }) => void
}

function EditEntryModal({ target, onClose, onSave }: IEditModalProps): React.JSX.Element {
  const { t } = useLingui()
  const [title, setTitle] = useState("")
  const [body, setBody] = useState("")

  useEffect(() => {
    if (target) {
      setTitle(target.entry.title)
      setBody(target.entry.body)
    }
  }, [target])

  return (
    <Modal opened={!!target} onClose={onClose} title={t`Edit entry`} size="lg">
      <Stack>
        <TextInput label={t`Title`} value={title} onChange={(ev) => setTitle(ev.currentTarget.value)} />
        <Textarea
          label={t`Body`}
          value={body}
          onChange={(ev) => setBody(ev.currentTarget.value)}
          autosize
          minRows={4}
          maxRows={12}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={() => onSave({ title, body })}>
            <Trans>Save</Trans>
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

interface IReassignModalProps {
  target: IPendingItem | null
  characters: ICharacterRef[]
  onClose: () => void
  onConfirm: (characterId: string) => void
}

function ReassignModal({ target, characters, onClose, onConfirm }: IReassignModalProps): React.JSX.Element {
  const { t } = useLingui()
  const [pick, setPick] = useState<string | null>(null)

  useEffect(() => {
    if (target && isJournal(target)) {
      setPick(null)
    }
  }, [target])

  const isJournalTarget = !!target && isJournal(target)
  const currentCharacterId = isJournalTarget ? target.entry.characterId : null

  return (
    <Modal opened={!!target} onClose={onClose} title={t`Reassign to a different character`} size="md">
      <Stack>
        <Text fz="sm" c="dimmed">
          <Trans>Pick the character this entry actually belongs to. The original attribution is preserved.</Trans>
        </Text>
        <Select
          label={t`Character`}
          placeholder={t`Choose a character`}
          data={characters.filter((c) => c.id !== currentCharacterId).map((c) => ({ value: c.id, label: c.name }))}
          value={pick}
          onChange={(v) => setPick(v)}
          searchable
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button disabled={!pick} onClick={() => pick && onConfirm(pick)}>
            <Trans>Reassign</Trans>
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
